import { SiteAvailability } from "@inrange/building-manager-api-client";
import { ReactNode, useState } from "react";
import { Alert, Button, Offcanvas, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import AvailabilityExplainModal from "./AvailabilityExplainModal";
import { formatAvailability, RYG_ICONS } from "./deploymentStatsUtils";

const AvailabilityBreakdownPanel = ({
  sites,
  aggregatePct,
  aggregateRyg,
  onClose,
  metricLabel = "PV availability",
  methodologyNote,
}: {
  sites: SiteAvailability[];
  aggregatePct: number | null;
  aggregateRyg: string;
  onClose: () => void;
  metricLabel?: string;
  // When provided, replaces the default PV-specific methodology alert + modal
  // (used for the BESS breakdown, which is graded on a 24-hour basis at 97%).
  methodologyNote?: ReactNode;
}) => {
  type SortField =
    | "site"
    | "lastMonth"
    | "last12Months"
    | "contracted"
    | "months"
    | "latestMonth";
  const [sortField, setSortField] = useState<SortField>("last12Months");
  const [sortAsc, setSortAsc] = useState(true);
  const [showAlert, setShowAlert] = useState(true);
  const [showExplain, setShowExplain] = useState(false);

  const numCmp = (a: number, b: number) => (sortAsc ? a - b : b - a);
  const strCmp = (a: string, b: string) =>
    sortAsc ? a.localeCompare(b) : b.localeCompare(a);

  const sorted = [...sites].sort((a, b) => {
    if (sortField === "site") return strCmp(a.siteName, b.siteName);
    if (sortField === "contracted")
      return numCmp(a.contractedPct ?? 999, b.contractedPct ?? 999);
    if (sortField === "months")
      return numCmp(a.monthsAnalysed ?? 0, b.monthsAnalysed ?? 0);
    if (sortField === "latestMonth")
      return strCmp(a.latestMonthLabel ?? "", b.latestMonthLabel ?? "");
    // Period-based sort (lastMonth / last12Months): null pct sorts last.
    const periodA = sortField === "lastMonth" ? a.lastMonth : a.last12Months;
    const periodB = sortField === "lastMonth" ? b.lastMonth : b.last12Months;
    const va = periodA?.pct ?? a.pct ?? 999;
    const vb = periodB?.pct ?? b.pct ?? 999;
    return numCmp(va, vb);
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(field === "site" || field === "latestMonth");
    }
  };
  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortAsc ? " ▲" : " ▼") : "";

  const headerStyle = { cursor: "pointer" as const };

  const renderPeriod = (
    period: { pct: number | null; ryg: string } | undefined,
    legacyPct: number | null | undefined,
    legacyRyg: string | undefined
  ) => {
    // Backend always sends the explicit period objects, but fall back to
    // the legacy flat fields if a stale response is hit during rollout.
    const pct = period?.pct ?? legacyPct ?? null;
    const ryg = period?.ryg ?? legacyRyg;
    return { pct, ryg };
  };

  return (
    <>
      <Offcanvas
        show
        placement="bottom"
        onHide={onClose}
        backdrop={false}
        scroll={true}
        enforceFocus={false}
        className="no-transition"
        style={{ height: "50vh" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {RYG_ICONS[aggregateRyg]} Aggregate {metricLabel}:{" "}
            {formatAvailability(aggregatePct)}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ overflow: "auto", padding: "0 16px 16px" }}>
          {showAlert && (
            <div className="mb-2">
              <Alert
                variant="info"
                dismissible
                onClose={() => setShowAlert(false)}
                className="small"
              >
                {methodologyNote ?? (
                  <>
                    <Button
                      variant="link"
                      className="p-0 text-muted"
                      onClick={() => setShowExplain(true)}
                    >
                      How is this calculated?
                    </Button>{" "}
                    <strong>Per-site RYG:</strong> graded against the site&apos;s
                    own contracted availability — green when measured ≥
                    contracted, amber within 1pp below, red more than 1pp below.
                    Sites without a contracted threshold fall back to the
                    fleet-wide 99 / 98 band; their <em>Contracted</em> column
                    shows the fallback value with a trailing <code>*</code>{" "}
                    (e.g. <code>99.00%*</code>). <strong>Aggregate RYG:</strong>{" "}
                    fixed fleet-wide thresholds (green ≥ 99%, amber 98 – 99%,
                    red &lt; 98%) since there&apos;s no single contract for the
                    fleet.
                  </>
                )}
              </Alert>
            </div>
          )}

          <Table striped hover size="sm">
            <thead>
              <tr>
                <th></th>
                <th style={headerStyle} onClick={() => toggleSort("site")}>
                  Site{sortIndicator("site")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("lastMonth")}
                >
                  Last month{sortIndicator("lastMonth")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("last12Months")}
                >
                  Last 12 months{sortIndicator("last12Months")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("contracted")}
                >
                  Contracted{sortIndicator("contracted")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("months")}
                >
                  Months{sortIndicator("months")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("latestMonth")}
                >
                  Latest month{sortIndicator("latestMonth")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((site) => {
                const lm = renderPeriod(site.lastMonth, undefined, undefined);
                const l12 = renderPeriod(site.last12Months, site.pct, site.ryg);
                const contractedSuffix = site.contractedPctIsDefault ? "*" : "";
                return (
                  <tr key={site.siteId}>
                    <td style={{ width: "30px" }}>
                      {RYG_ICONS[l12.ryg ?? site.ryg] ?? ""}
                    </td>
                    <td>
                      <Link to={`/site/${site.siteId}/health`}>
                        {site.siteName}
                      </Link>
                    </td>
                    <td className="text-end">
                      {lm.ryg && RYG_ICONS[lm.ryg]}{" "}
                      {formatAvailability(lm.pct)}
                    </td>
                    <td className="text-end">
                      {l12.ryg && RYG_ICONS[l12.ryg]}{" "}
                      {formatAvailability(l12.pct)}
                    </td>
                    <td className="text-end">
                      {site.contractedPct != null
                        ? `${site.contractedPct.toFixed(2)}%${contractedSuffix}`
                        : ""}
                    </td>
                    <td className="text-end">{site.monthsAnalysed ?? ""}</td>
                    <td className="text-end">{site.latestMonthLabel ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Offcanvas.Body>
      </Offcanvas>
      {!methodologyNote && (
        <AvailabilityExplainModal
          show={showExplain}
          onHide={() => setShowExplain(false)}
        />
      )}
    </>
  );
};

export default AvailabilityBreakdownPanel;
